import { Alert, AlertTitle, Box, Button, Stack, Typography } from '@mui/material';
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;

  /** Changing this remounts the boundary, clearing a caught error. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

/**
 * Catches a render-time error so one broken page cannot take the whole application down.
 *
 * @remarks
 * There was no boundary at all until a task whose source record had been deleted was opened
 * and the report came back as "the system becomes unavailable". That is what React does
 * without one: an error thrown while rendering unmounts the entire tree, and the person is
 * left looking at an empty page with nothing to read, nothing to copy, and no way to tell
 * anyone what happened beyond the fact that it stopped working.
 *
 * So the point of this is not to hide the failure. It is to make the failure *say something*.
 * The message is shown rather than swallowed, because the alternative is a bug report that
 * consists of the word "broken", and this one cost several days of guessing.
 *
 * It has to be a class. Error boundaries are the one thing React still has no hook for.
 */
export class ErrorBoundary extends Component<Props, State> {
  public constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  public static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  public componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console is where a developer looks and where a browser's own error reporting picks
    // it up. The component stack is the half that says *which* page failed, and it is not in
    // the error's own stack.
    console.error('A page failed to render.', error, info.componentStack);
  }

  public componentDidUpdate(previous: Props): void {
    // Navigating away from a page that threw must not leave its error on screen. Without this
    // the boundary is a trap: one bad task makes every subsequent page look broken too.
    if (this.state.error !== null && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  public override render(): ReactNode {
    const { error } = this.state;

    if (error === null) {
      return this.props.children;
    }

    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Alert severity="error" variant="outlined">
          <AlertTitle>این صفحه باز نشد</AlertTitle>

          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography variant="body2">
              خطایی در نمایش این صفحه رخ داد. بقیهٔ بخش‌های سامانه کار می‌کنند و می‌توانید از
              منوی بالا به صفحهٔ دیگری بروید.
            </Typography>

            {/* Shown, not hidden behind a details toggle. The person reading this is the only
                one who can pass it on, and a message they cannot see is a message that does
                not reach anybody. */}
            <Typography
              variant="caption"
              className="mono"
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'action.hover',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                direction: 'ltr',
                textAlign: 'left',
              }}
            >
              {error.message || String(error)}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              این متن را برای پشتیبانی بفرستید.
            </Typography>

            <Box>
              <Button variant="outlined" size="small" onClick={() => window.location.reload()}>
                بارگذاری دوباره
              </Button>
            </Box>
          </Stack>
        </Alert>
      </Box>
    );
  }
}
